"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
	addProductToList,
	createList,
	getUserLists
} from "@/app/actions/wishlist";
import { useConsultantStore, useSessionStore, useUserStore } from "@/app/store";
import type {
	CreateListListFor,
	CreateListProductError,
	CreateListShippingAddress,
	ListKind,
	WishlistSummary
} from "@/domain/wishlist";
import {
	WISHLIST_OCCASIONS,
	type WishlistOccasion
} from "@/domain/wishlist/occasions";

export type AddToListStatus = "ready" | "error";

export type CreateListStatus = "idle" | "submitting" | "success" | "error";

export interface UseAddToListUrls {
	login: string;
	createWishlist: string;
	createRegistry: string;
	listViewBase: string;
}

export interface UseAddToListParams {
	sku: string;
	quantity: number;
	urls: UseAddToListUrls;
	createPanelEnabled?: boolean;
}

export type CreateListCountry = "US" | "CA";

export interface CreateListValues {
	name: string;
	occasion: string;
	eventDate?: string;
	isPrivate: boolean;
	listFor: CreateListListFor;
	shippingAddress?: CreateListShippingAddress;
}

const CREATE_LIST_PARAM = "createList";

function isListKind(value: string | null): value is ListKind {
	return value === "wishlist" || value === "registry";
}

function withCreateListParam(search: string, kind: ListKind): string {
	const params = new URLSearchParams(search);
	params.set(CREATE_LIST_PARAM, kind);
	return `?${params.toString()}`;
}

function withoutCreateListParam(search: string): string {
	const params = new URLSearchParams(search);
	params.delete(CREATE_LIST_PARAM);
	const query = params.toString();
	return query ? `?${query}` : "";
}

export interface AssignedConsultant {
	consultantNumber: string;
	name: string;
	location?: string;
}

export interface ConnectConsultantState {
	isOpen: boolean;
	onOpenChange: (next: boolean) => void;
	onSelect: (consultant: AssignedConsultant) => void;
}

export interface CreatedListState {
	pk: string;
	name: string;
	kind: ListKind;
	productAdded: boolean;
	productError?: CreateListProductError;
}

export interface CreateListPanelState {
	kind: ListKind;
	isOpen: boolean;
	status: CreateListStatus;
	occasions: readonly WishlistOccasion[];
	country: CreateListCountry;
	consultant?: AssignedConsultant;
	errorMessage?: string;
	createdList?: CreatedListState;
	viewListHref?: string;
	canCreateForOthers: boolean;
	onOpenChange: (next: boolean) => void;
	onSubmit: (values: CreateListValues) => void;
	onFindConsultant: () => void;
	onRemoveConsultant: () => void;
}

export interface UseAddToList {
	isOpen: boolean;
	status: AddToListStatus;
	wishlists: WishlistSummary[];
	registries: WishlistSummary[];
	submittingPk?: string;
	addedPk?: string;
	addedListName?: string;
	errorMessage?: string;
	viewListHref?: string;
	open: () => void;
	close: () => void;
	setOpen: (next: boolean) => void;
	onSelect: (pk: string) => void;
	onCreateNew: (kind: ListKind) => void;
	createPanel: CreateListPanelState;
	connectConsultant: ConnectConsultantState;
}

export function useAddToList(params: UseAddToListParams): UseAddToList {
	const { sku, quantity, urls, createPanelEnabled = false } = params;

	const userType = useUserStore((state) => state.getUserType());
	const sessionState = useSessionStore((state) => state.sessionState);
	const consultantState = useConsultantStore(
		(state) => state.consultantState
	);
	const country: CreateListCountry =
		sessionState?.locale === "en-CA" ? "CA" : "US";

	const [isOpen, setIsOpen] = useState(false);
	const [status, setStatus] = useState<AddToListStatus>("ready");
	const [wishlists, setWishlists] = useState<WishlistSummary[]>([]);
	const [registries, setRegistries] = useState<WishlistSummary[]>([]);
	const [submittingPk, setSubmittingPk] = useState<string | undefined>();
	const [addedPk, setAddedPk] = useState<string | undefined>();
	const [addedListName, setAddedListName] = useState<string | undefined>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [createKind, setCreateKind] = useState<ListKind>("wishlist");
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [createStatus, setCreateStatus] = useState<CreateListStatus>("idle");
	const [createErrorMessage, setCreateErrorMessage] = useState<
		string | undefined
	>();
	const [createdList, setCreatedList] = useState<
		CreatedListState | undefined
	>();
	const [assignedConsultant, setAssignedConsultant] = useState<
		AssignedConsultant | undefined
	>();
	const [isConnectOpen, setIsConnectOpen] = useState(false);

	const redirect = useCallback((href: string) => {
		window.location.href = href;
	}, []);

	const goToLogin = useCallback(
		(target?: string) => {
			const dest =
				target ?? window.location.pathname + window.location.search;
			redirect(
				`${urls.login}?loginRedirectTarget=${encodeURIComponent(dest)}`
			);
		},
		[redirect, urls.login]
	);

	const loadLists = useCallback(() => {
		if (!sessionState) {
			return;
		}

		void getUserLists(sessionState).then((response) => {
			if (response.status === "failed") {
				setStatus("error");
				setErrorMessage(response.message);
				return;
			}

			setWishlists(response.wishlists);
			setRegistries(response.registries);
			setStatus("ready");
		});
	}, [sessionState]);

	const prefetchedRef = useRef(false);
	useEffect(() => {
		if (prefetchedRef.current) {
			return;
		}
		if (userType === "anonymous" || !sessionState) {
			return;
		}
		prefetchedRef.current = true;
		loadLists();
	}, [userType, sessionState, loadLists]);

	const open = useCallback(() => {
		setErrorMessage(undefined);
		setAddedPk(undefined);
		setAddedListName(undefined);
		setSubmittingPk(undefined);
		setIsOpen(true);
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
	}, []);

	const setOpen = useCallback(
		(next: boolean) => {
			if (next) {
				open();
				return;
			}
			close();
		},
		[open, close]
	);

	const onSelect = useCallback(
		(pk: string) => {
			if (!sessionState) {
				goToLogin();
				return;
			}

			setErrorMessage(undefined);
			setSubmittingPk(pk);

			void addProductToList(sessionState, {
				wishlistId: pk,
				sku,
				quantity
			}).then((response) => {
				setSubmittingPk(undefined);

				if (response.status === "success") {
					const match = [...wishlists, ...registries].find(
						(list) => list.pk === pk
					);
					setAddedPk(pk);
					setAddedListName(match?.name);
					return;
				}

				if (response.reason === "session-expired") {
					goToLogin();
					return;
				}

				setErrorMessage(response.message);
			});
		},
		[sessionState, goToLogin, sku, quantity, wishlists, registries]
	);

	const onCreateNew = useCallback(
		(kind: ListKind) => {
			if (!createPanelEnabled) {
				const base =
					kind === "registry"
						? urls.createRegistry
						: urls.createWishlist;
				const target = `${base}?product=${encodeURIComponent(sku)}&qty=${quantity}`;
				if (userType === "anonymous") {
					goToLogin(target);
					return;
				}
				redirect(target);
				return;
			}

			// Anonymous users can open the dropdown, but creating a list
			// requires auth — send them to login and reopen the sheet after.
			if (userType === "anonymous") {
				goToLogin(
					window.location.pathname +
						withCreateListParam(window.location.search, kind)
				);
				return;
			}

			setIsOpen(false);
			setCreateKind(kind);
			setCreateStatus("idle");
			setCreateErrorMessage(undefined);
			setCreatedList(undefined);
			setAssignedConsultant(connectedConsultant(consultantState));
			setIsConnectOpen(false);
			setIsCreateOpen(true);
		},
		[
			createPanelEnabled,
			urls.createRegistry,
			urls.createWishlist,
			sku,
			quantity,
			userType,
			goToLogin,
			redirect,
			consultantState
		]
	);

	const resumedRef = useRef(false);
	useEffect(() => {
		if (
			!createPanelEnabled ||
			resumedRef.current ||
			userType === "anonymous" ||
			!sessionState
		) {
			return;
		}
		const kind = new URLSearchParams(window.location.search).get(
			CREATE_LIST_PARAM
		);
		if (!isListKind(kind)) {
			return;
		}
		resumedRef.current = true;
		window.history.replaceState(
			window.history.state,
			"",
			window.location.pathname +
				withoutCreateListParam(window.location.search) +
				(window.location.hash ?? "")
		);
		onCreateNew(kind);
	}, [createPanelEnabled, userType, sessionState, onCreateNew]);

	const onFindConsultant = useCallback(() => {
		setIsConnectOpen(true);
	}, []);

	const onRemoveConsultant = useCallback(() => {
		setAssignedConsultant(undefined);
	}, []);

	const onConnectOpenChange = useCallback((next: boolean) => {
		setIsConnectOpen(next);
	}, []);

	const onConsultantSelected = useCallback(
		(consultant: AssignedConsultant) => {
			setAssignedConsultant(consultant);
			setIsConnectOpen(false);
		},
		[]
	);

	const onCreateOpenChange = useCallback(
		(next: boolean) => {
			if (next) {
				onCreateNew(createKind);
				return;
			}
			if (createStatus === "submitting") {
				return;
			}
			setIsCreateOpen(false);
		},
		[onCreateNew, createKind, createStatus]
	);

	const onCreateSubmit = useCallback(
		(values: CreateListValues) => {
			if (!sessionState) {
				goToLogin();
				return;
			}

			setCreateStatus("submitting");
			setCreateErrorMessage(undefined);

			void createList(sessionState, {
				kind: createKind,
				name: values.name,
				occasion: values.occasion,
				eventDate: values.eventDate,
				isPrivate: values.isPrivate,
				listFor: values.listFor,
				product: { sku, quantity },
				consultant: assignedConsultant
					? {
							mode: "selected",
							consultantNumber:
								assignedConsultant.consultantNumber
						}
					: { mode: "none" },
				...(values.shippingAddress
					? { shippingAddress: values.shippingAddress }
					: {})
			}).then((response) => {
				if (response.status === "success") {
					const summary = {
						pk: response.list.pk,
						name: response.list.name
					};
					if (response.list.kind === "registry") {
						setRegistries((current) => [...current, summary]);
					} else {
						setWishlists((current) => [...current, summary]);
					}
					if (response.productAdded) {
						setAddedPk(summary.pk);
						setAddedListName(summary.name);
					}
					setCreatedList({
						...summary,
						kind: response.list.kind,
						productAdded: response.productAdded,
						productError: response.productError
					});
					setCreateStatus("success");
					return;
				}

				if (response.reason === "session-expired") {
					goToLogin();
					return;
				}

				setCreateErrorMessage(response.message);
				setCreateStatus("error");
			});
		},
		[sessionState, goToLogin, createKind, sku, quantity, assignedConsultant]
	);

	const viewListHref = addedPk
		? `${urls.listViewBase}?wishlistId=${encodeURIComponent(addedPk)}`
		: undefined;

	const createdListHref = createdList
		? `${urls.listViewBase}?wishlistId=${encodeURIComponent(createdList.pk)}`
		: undefined;

	return {
		isOpen,
		status,
		wishlists,
		registries,
		submittingPk,
		addedPk,
		addedListName,
		errorMessage,
		viewListHref,
		open,
		close,
		setOpen,
		onSelect,
		onCreateNew,
		createPanel: {
			kind: createKind,
			isOpen: isCreateOpen,
			status: createStatus,
			occasions: WISHLIST_OCCASIONS,
			country,
			consultant: assignedConsultant,
			errorMessage: createErrorMessage,
			createdList,
			viewListHref: createdListHref,
			canCreateForOthers: userType === "consultant",
			onOpenChange: onCreateOpenChange,
			onSubmit: onCreateSubmit,
			onFindConsultant,
			onRemoveConsultant
		},
		connectConsultant: {
			isOpen: isConnectOpen,
			onOpenChange: onConnectOpenChange,
			onSelect: onConsultantSelected
		}
	};
}

function connectedConsultant(
	consultantState: ReturnType<
		typeof useConsultantStore.getState
	>["consultantState"]
): AssignedConsultant | undefined {
	if (consultantState.status !== "connected") {
		return undefined;
	}
	const name =
		`${consultantState.firstName} ${consultantState.lastName}`.trim();
	const location = [
		consultantState.address?.city,
		consultantState.address?.state
	]
		.filter(Boolean)
		.join(", ");
	return {
		consultantNumber: consultantState.consultantNumber,
		name,
		...(location ? { location } : {})
	};
}
